/**
 * Chrome specific to the bar chart: the hover readout.
 *
 * The legend and data table are shared with the line chart and live in
 * ../cartesianChrome; they are re-exported here so this folder is the one place
 * the bar chart reaches for its chrome. The tooltip is its own because a bar
 * reads differently from a line: the hovered category lists its bars in the
 * order they are drawn, and a stack owes the reader its total.
 */

import { cn } from "../../ui/utils";
import type { ChartSeries } from "../cartesianUtils";
import type { BarHighlight, BarPoint } from "./barUtils";

export { SeriesLegend, SeriesTable } from "../cartesianChrome";

interface BarTooltipProps {
  readonly active?: boolean;
  readonly label?: string | number;
  readonly payload?: ReadonlyArray<{
    readonly dataKey?: string | number;
    readonly value?: unknown;
    readonly payload?: { readonly x?: unknown };
  }>;
  readonly series: readonly ChartSeries[];
  readonly unit: string;
  /** Stacked charts add a total row, since the whole bar is a quantity too. */
  readonly showTotal: boolean;
  /** The series under the cursor, emphasised so the reader knows which bar answered. */
  readonly activeKey?: string | null;
  readonly valueFormatter: (value: number) => string;
  readonly barHighlight?: (point: BarPoint) => BarHighlight | undefined;
}

/**
 * Readout for the hovered category. Rows keep the series order rather than
 * sorting by size, so they line up with the bars on screen. Text wears ink
 * tokens; identity comes from the coloured swatch beside it.
 */
export function BarTooltip({
  active,
  label,
  payload,
  series,
  unit,
  showTotal,
  activeKey,
  valueFormatter,
  barHighlight,
}: Readonly<BarTooltipProps>) {
  if (!active || !payload?.length) return null;

  const point = payload.find(
    (item) => typeof item?.payload?.x === "string",
  )?.payload as unknown as BarPoint | undefined;
  let heading = "";
  if (typeof point?.x === "string" && point.x) {
    heading = point.x;
  } else if (label != null) {
    heading = String(label);
  }
  const swatch = (fallback: string) =>
    point && barHighlight ? barHighlight(point)?.stroke || fallback : fallback;

  const rows = series
    .map((entry) => {
      const match = payload.find((item) => item?.dataKey === entry.key);
      const value = typeof match?.value === "number" ? match.value : Number(match?.value);
      return Number.isFinite(value)
        ? { id: entry.id, key: entry.key, label: entry.label, color: swatch(entry.color), value }
        : null;
    })
    .filter(
      (row): row is { id: string; key: string; label: string; color: string; value: number } =>
        row != null,
    );

  if (!rows.length) return null;

  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <div className="max-w-[240px] rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
      <p className="mb-1 break-words text-xs font-bold leading-snug text-foreground">{heading}</p>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li
            key={row.id}
            className={cn(
              "flex items-center gap-2",
              // Dimming the rest is only meaningful once there is a rest.
              activeKey != null && rows.length > 1 && activeKey !== row.key && "opacity-55",
            )}
          >
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[3px]"
              style={{ backgroundColor: row.color }}
            />
            <span className="min-w-0 flex-1 break-words text-[11px] font-medium leading-snug text-muted-foreground">
              {row.label}
            </span>
            <span className="shrink-0 text-[11px] font-bold tabular-nums text-foreground">
              {valueFormatter(row.value)}
              {unit}
            </span>
          </li>
        ))}
      </ul>

      {showTotal && rows.length > 1 && (
        <div className="mt-1.5 flex items-center gap-2 border-t border-border pt-1.5">
          <span className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-muted-foreground">
            Total
          </span>
          <span className="shrink-0 text-[11px] font-bold tabular-nums text-foreground">
            {valueFormatter(total)}
            {unit}
          </span>
        </div>
      )}
    </div>
  );
}
